// src/shared/changelog/ChangelogNewItemsContext.tsx
import React, { createContext, useContext } from "react";
import { useChangelogNewItems } from "./useChangelogNewItems";

/**
 * The menu-item ids currently wearing a `New` tag (CTX-511).
 *
 * A context rather than a prop threaded down, because the consumer is
 * `MenuItem` — a recursive component reached through `SideMenuBody` →
 * `SideMenuNavByProduct` → `MenuSection` → itself. Drilling would add the same
 * prop to four components that have no use for it, and to every one of their
 * call sites.
 *
 * It is deliberately **not** a field on `MenuData`'s `MenuItem`, beside
 * `badgeBeta`. That field is authored: a person types `badgeBeta: "Beta"` into
 * the tree and it stays until someone deletes it. This one is derived per
 * session from the feed and changes as the user walks around the app, so
 * putting it on the static tree would mean cloning the tree on every render to
 * carry a value no author ever writes.
 *
 * The empty default is what makes every existing test keep passing: a
 * `MenuItem` rendered outside the provider simply has no tags.
 */
/**
 * Exported so a test can supply the set directly. A rail test is about what a
 * row does with the answer, not about how the answer was derived — going
 * through the provider would drag a mocked feed and a fake clock into every
 * one of them, and the derivation has its own tests.
 */
export const ChangelogNewItemsContext = createContext<Set<string>>(new Set());

export function ChangelogNewItemsProvider({
  pathname,
  tenantType,
  children,
}: {
  /** Current route — clears the tag of whichever item it belongs to. */
  pathname: string;
  /** Decides whether tags exist at all — see `canSeeChangelog`. */
  tenantType: string;
  children: React.ReactNode;
}): React.ReactElement {
  const newItemIds = useChangelogNewItems(pathname, tenantType);
  return (
    <ChangelogNewItemsContext.Provider value={newItemIds}>
      {children}
    </ChangelogNewItemsContext.Provider>
  );
}

/** The ids wearing a `New` tag; empty outside the provider. */
export function useChangelogNewItemIds(): Set<string> {
  return useContext(ChangelogNewItemsContext);
}
